import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import styles from './Layout.module.css';

interface LayoutProps {
  children: React.ReactNode;
}

type NotifKey = 'comptes' | 'commandes' | 'produits';

const NAV_ITEMS: { path: string; label: string; icon: string; notifKey?: NotifKey }[] = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/commandes', label: 'Commandes', icon: '🛒', notifKey: 'commandes' },
  { path: '/produits', label: 'Produits', icon: '📦', notifKey: 'produits' },
  { path: '/comptes', label: 'Comptes', icon: '👤', notifKey: 'comptes' },
  { path: '/reductions', label: 'Réductions', icon: '🎟️' },
  { path: '/import-facture', label: 'Import Facture', icon: '📄' },
  { path: '/inventaire',    label: 'Inventaire',      icon: '🔢' },
  { path: '/categories',    label: 'Catégories',      icon: '🏷️' },
  { path: '/regles',        label: 'Règles auto',     icon: '⚡' },
  { path: '/statistiques',  label: 'Statistiques',   icon: '📈' },
  { path: '/recettes',      label: 'Recettes',        icon: '💰' },
  { path: '/avis',          label: 'Avis produits',   icon: '⭐' },
  { path: '/banners',       label: 'Bannières',        icon: '🖼️' },
  { path: '/ventes',        label: 'Ventes',           icon: '🧾' },
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { counts, dismiss } = useNotifications();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = (path: string, notifKey?: NotifKey) => {
    if (notifKey && counts[notifKey] > 0) dismiss(notifKey);
    navigate(path);
  };

  return (
    <div className={styles.layout}>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <div className={styles.brandName}>E-Liquide Sud</div>
          <div className={styles.brandSub}>Administration</div>
        </div>

        <nav className={styles.nav}>
          {NAV_ITEMS.map((item) => {
            const badge = item.notifKey ? counts[item.notifKey] : 0;
            return (
              <button
                key={item.path}
                className={`${styles.navItem} ${location.pathname === item.path ? styles.navItemActive : ''}`}
                onClick={() => handleNavClick(item.path, item.notifKey)}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                <span className={styles.navLabel}>{item.label}</span>
                {badge > 0 && (
                  <span className={styles.badge}>{badge > 99 ? '99+' : badge}</span>
                )}
              </button>
            );
          })}
        </nav>

        <div className={styles.sidebarFooter}>
          {user && (
            <div className={styles.userInfo}>
              <div className={styles.userName}>{user.FirstName} {user.LastName}</div>
              <div className={styles.userRole}>{user.Role}</div>
            </div>
          )}
          <button onClick={handleLogout} className={styles.logoutButton}>
            🚪 Déconnexion
          </button>
        </div>
      </aside>

      <main className={styles.main}>
        {children}
      </main>
    </div>
  );
};
